import { useState, useEffect, useRef } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

export default function Login({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canvasRef = useRef(null);
  const twRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError("Invalid username or password");
        return;
      }
      const payload = JSON.parse(atob(data.token.split(".")[1]));
      const user = { id: payload.user_id, username: payload.username, role: payload.role, company_id: payload.company_id };
      localStorage.setItem("expertnet_token", data.token);
      localStorage.setItem("expertnet_user", JSON.stringify(user));
      onLogin(user);
    } catch {
      setError("Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const AURORA_FRAG = `
void main(){
  vec2 uv = gl_FragCoord.xy / u_res;
  float aspect = u_res.x / u_res.y;
  vec2 p = vec2((uv.x-0.5)*aspect, uv.y-0.5);
  vec2 m = fxPoint()-0.5; m.x *= aspect;
  float t = u_time * 0.3;
  vec3 col = u_c0 * 0.5;
  for(int i=0;i<6;i++){
    float fi = float(i);
    float n  = fbm(vec2(p.x*1.3 + fi*4.0, t*0.45 + fi));
    float cy = -0.40 + fi*0.155 + (n-0.5)*0.34 + sin(t*0.5+fi*1.6)*0.04;
    cy += m.y * 0.15 * exp(-pow((p.x - m.x)*1.15, 2.0));
    float d = abs(p.y - cy);
    float band = exp(-d*d*200.0);
    float glow = exp(-d*d*26.0) * 0.22;
    float shimmer = 0.8 + 0.2*sin(p.x*5.0 - t*2.0 + fi);
    vec3 c = ramp(0.44 + fi*0.08 + (n-0.5)*0.3);
    col += c * (band*0.6*shimmer + glow*0.55);
  }
  vec2 rip = ripples(uv);
  col += u_c4 * rip.y * 0.7;
  float md = length(p - m);
  col += u_c3 * exp(-md*md*7.0) * u_mouseIn * 0.4;
  col *= 0.5 + u_intensity*0.16;
  col += (hash21(gl_FragCoord.xy + u_time) - 0.5) * 0.04 * u_grain;
  gl_FragColor = vec4(max(col, 0.0), 1.0);
}
    `;

    const hex01 = (h) => { const n = parseInt(h.slice(1), 16); return [((n>>16)&255)/255, ((n>>8)&255)/255, (n&255)/255]; };
    const PALETTE = ['#04141a','#073038','#0e7490','#2dd4bf','#9bf6e6'].map(hex01);

    let bg = null;
    let cancelTW = () => {};

    const timer = setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      if (typeof window.createGLBackground !== 'function') return;

      bg = window.createGLBackground(canvas, AURORA_FRAG, {
        palette: PALETTE,
        speed: 1,
        intensity: 0.9,
        grain: 0.5,
        scale: 0.85,
      });

      const container = twRef.current;
      if (container) {
        const LINES = ['The sourcing layer for expert networks.', 'Source. Reach out. Track. All in one place.'];
        const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
        const l1 = document.createElement('div'); l1.className = 'tw-line';
        const l2 = document.createElement('div'); l2.className = 'tw-line';
        container.innerHTML = '';
        container.append(l1, l2);
        if (reduce) {
          l1.textContent = LINES[0];
          l2.textContent = LINES[1];
        } else {
          let cancelled = false;
          const wait = (ms) => new Promise((r) => setTimeout(r, ms));
          const rand = (a, b) => a + Math.random() * (b - a);
          (async () => {
            for (let i = 1; i <= LINES[0].length; i++) {
              if (cancelled) return;
              l1.innerHTML = LINES[0].slice(0, i) + '<span class="tw-caret"></span>';
              await wait(rand(45, 60));
            }
            l1.textContent = LINES[0];
            await wait(550);
            if (cancelled) return;
            for (let i = 1; i <= LINES[1].length; i++) {
              if (cancelled) return;
              l2.innerHTML = LINES[1].slice(0, i) + '<span class="tw-caret"></span>';
              await wait(rand(45, 60));
            }
          })();
          cancelTW = () => { cancelled = true; };
        }
      }
    }, 50);

    return () => {
      clearTimeout(timer);
      cancelTW();
      if (bg) bg.destroy();
    };
  }, []);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      display: 'grid',
      gridTemplateColumns: '420px 1fr',
      fontFamily: "'Inter', sans-serif",
    }}>
      <style>{`
        .tw-line { min-height: 1.5em; }
        .tw-line + .tw-line { margin-top: 28px; }
        .tw-caret {
          display: inline-block;
          width: 2px;
          height: 1.05em;
          border-radius: 1px;
          background: rgba(255,255,255,0.95);
          margin-left: 3px;
          vertical-align: -0.16em;
          animation: tw-blink 1s steps(1, end) infinite;
        }
        @keyframes tw-blink { 0%, 50% { opacity: 1; } 50.01%, 100% { opacity: 0; } }
        @media (prefers-reduced-motion: reduce) { .tw-caret { display: none; animation: none; } }
      `}</style>

      {/* Left panel */}
      <div style={{ background: '#ffffff', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', padding: '48px 48px 40px', position: 'relative', zIndex: 2, boxShadow: 'rgba(0,0,0,0.4) 4px 0px 24px' }}>

        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="30" height="30" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="rasp-login-g" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#17b3a6" />
                  <stop offset="1" stopColor="#0e7490" />
                </linearGradient>
              </defs>
              <rect x="2" y="2" width="40" height="40" rx="12" fill="url(#rasp-login-g)" />
              <circle cx="22" cy="18" r="6.4" fill="none" stroke="#fff" strokeWidth="3" />
              <path d="M22 24.4v6.2" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
              <path d="M22 30.6l5 4M22 30.6l-5 4" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
            </svg>
            <span style={{ fontWeight: 500, fontSize: 19, color: '#0a1628', letterSpacing: '-0.02em' }}>Rasp</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <h1 style={{ fontWeight: 500, fontSize: 24, color: '#0a1628', letterSpacing: '-0.01em', marginBottom: 5 }}>Sign in</h1>
          <p style={{ fontSize: 13.5, color: '#51697d', marginBottom: 28 }}>Welcome back. Enter your credentials to continue.</p>

          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#51697d', display: 'block', marginBottom: 7 }}>Username</label>
            <input type="text" value={username} onChange={e => setUsername(e.target.value)} autoComplete="username" required
              style={{ width: '100%', padding: '11px 12px', background: '#f6f8fa', border: '1px solid #e2e8ef', borderRadius: 8, fontSize: 13.5, color: '#0a1628', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
              onFocus={e => { e.currentTarget.style.borderColor = '#0d9488'; e.currentTarget.style.boxShadow = 'rgba(13,148,136,0.12) 0 0 0 3px'; }}
              onBlur={e => { e.currentTarget.style.borderColor = '#e2e8ef'; e.currentTarget.style.boxShadow = 'none'; }}
            />
          </div>

          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#51697d', display: 'block', marginBottom: 7 }}>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" required
              style={{ width: '100%', padding: '11px 12px', background: '#f6f8fa', border: '1px solid #e2e8ef', borderRadius: 8, fontSize: 13.5, color: '#0a1628', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
              onFocus={e => { e.currentTarget.style.borderColor = '#0d9488'; e.currentTarget.style.boxShadow = 'rgba(13,148,136,0.12) 0 0 0 3px'; }}
              onBlur={e => { e.currentTarget.style.borderColor = '#e2e8ef'; e.currentTarget.style.boxShadow = 'none'; }}
            />
          </div>

          {error && (
            <div style={{ fontSize: 12, color: '#c0432f', background: 'rgba(224,106,90,0.1)', border: '1px solid rgba(224,106,90,0.3)', borderRadius: 8, padding: '9px 12px', marginBottom: 16 }}>{error}</div>
          )}

          <button type="submit" disabled={loading}
            style={{ width: '100%', padding: '12px 0', border: 'none', borderRadius: 8, background: 'linear-gradient(135deg, #17b3a6 0%, #0e7490 100%)', color: '#ffffff', fontWeight: 500, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, boxShadow: 'rgba(14,116,144,0.4) 0 1px 2px', fontFamily: 'inherit' }}>
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <div style={{ marginTop: 40, textAlign: 'center' }}>
          <span style={{ fontSize: 11, color: '#93a4b3' }}>By signing in you agree to the Terms of Service</span>
        </div>

      </div>

      {/* Right panel */}
      <div style={{
        flex: 1,
        position: 'relative',
        overflow: 'hidden',
        background: '#04141a',
      }}>
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            display: 'block',
            touchAction: 'none',
          }}
        />
        <div style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '60%',
          height: '42%',
          background: 'linear-gradient(to bottom right, rgba(0,0,0,0.30), rgba(0,0,0,0) 70%)',
          pointerEvents: 'none',
        }} />
        <div
          ref={twRef}
          style={{
            position: 'absolute',
            left: 48,
            top: 56,
            maxWidth: 380,
            pointerEvents: 'none',
            color: 'rgba(255,255,255,0.95)',
            fontFamily: "'Inter', system-ui, sans-serif",
            fontWeight: 500,
            fontSize: 24,
            lineHeight: 1.5,
            letterSpacing: '-0.01em',
            textShadow: '0 1px 14px rgba(0,0,0,0.38)',
          }}
        />
      </div>

    </div>
  );
}
